/*
  Free magnesium calculator (sections/nr-magnesium-tool.liquid).

  Step 1 looks up the UK daily amount for the chosen age group and sex, adds
  the extra for pregnancy or breastfeeding, and compares it with the 375 mg
  NRV that labels use. Step 2 turns a compound weight into magnesium with
  each form's share of weight. Step 3 lists the forms ticked for a goal. The
  foods table gains a column for each portion's share of the daily amount.

  Choices are kept in localStorage on the visitor's own device only.
*/
if (!customElements.get('nr-magnesium-tool')) {
  customElements.define(
    'nr-magnesium-tool',
    class NrMagnesiumTool extends HTMLElement {
      connectedCallback() {
        const configElement = this.querySelector('[data-config]');
        if (!configElement) return;
        try {
          this.config = JSON.parse(configElement.textContent);
        } catch (error) {
          return;
        }
        this.ages = new Map(this.config.ages.map((age) => [age.id, age]));
        this.forms = new Map(this.config.forms.map((form) => [form.id, form]));
        if (this.ages.size === 0) return;

        this.el = {
          app: this.querySelector('[data-app]'),
          age: this.querySelector('[data-age]'),
          sex: Array.from(this.querySelectorAll('[data-sex]')),
          stageField: this.querySelector('[data-stage-field]'),
          stage: this.querySelector('[data-stage]'),
          amount: this.querySelector('[data-amount]'),
          supplementStat: this.querySelector('[data-supplement-stat]'),
          notes: this.querySelector('[data-notes]'),
          form: this.querySelector('[data-form]'),
          compound: this.querySelector('[data-compound]'),
          labelResult: this.querySelector('[data-label-result]'),
          goals: Array.from(this.querySelectorAll('[data-goal]')),
          goalResult: this.querySelector('[data-goal-result]'),
          foodShareHead: this.querySelector('[data-food-share-head]'),
          foodShares: Array.from(this.querySelectorAll('[data-food-share]')),
        };

        this.restore();
        this.bind();
        this.render();
        this.el.app.classList.add('is-ready');
      }

      /* ---------- State ---------- */

      restore() {
        let saved = null;
        try {
          saved = JSON.parse(window.localStorage.getItem(NrMagnesiumTool.storageKey) || 'null');
        } catch (error) {
          saved = null;
        }
        if (!saved) return;

        if (this.ages.has(saved.age)) this.el.age.value = saved.age;
        this.el.sex.forEach((input) => {
          input.checked = input.value === saved.sex;
        });
        if (!this.sex()) this.el.sex[0].checked = true;
        if (['none', 'pregnant', 'breastfeeding'].includes(saved.stage)) this.el.stage.value = saved.stage;
        if (this.el.form && this.forms.has(saved.form)) this.el.form.value = saved.form;
        if (this.el.compound && Number(saved.compound) > 0) this.el.compound.value = saved.compound;
        this.el.goals.forEach((input) => {
          if (input.value === saved.goal) input.checked = true;
        });
      }

      save() {
        try {
          window.localStorage.setItem(
            NrMagnesiumTool.storageKey,
            JSON.stringify({
              age: this.el.age.value,
              sex: this.sex(),
              stage: this.el.stage.value,
              form: this.el.form ? this.el.form.value : '',
              compound: this.el.compound ? this.el.compound.value : '',
              goal: this.goal(),
            })
          );
        } catch (error) {
          /* Private browsing or storage turned off: the tool still works. */
        }
      }

      bind() {
        this.addEventListener('change', (event) => {
          if (!event.target.closest('[data-app]')) return;
          this.track();
          this.update();
        });

        if (this.el.compound) {
          this.el.compound.addEventListener('input', () => {
            this.track();
            this.update();
          });
        }

        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-nr-tool-product]');
          if (link) NrMagnesiumTool.publish('nr_tool_product_click', { tool: 'magnesium', product: link.dataset.nrToolProduct });
        });
      }

      update() {
        this.save();
        this.render();
      }

      track() {
        if (this.tracked) return;
        this.tracked = true;
        NrMagnesiumTool.publish('nr_tool_use', { tool: 'magnesium' });
      }

      sex() {
        const picked = this.el.sex.find((input) => input.checked);
        return picked ? picked.value : '';
      }

      goal() {
        const picked = this.el.goals.find((input) => input.checked);
        return picked ? picked.value : '';
      }

      /* ---------- Maths ---------- */

      // The UK daily amount for the current choices, in mg.
      dailyAmount() {
        const age = this.ages.get(this.el.age.value) || this.config.ages[0];
        const female = this.sex() !== 'male';
        let amount = Number(female ? age.female : age.male) || 0;
        if (this.askStage(age)) {
          if (this.el.stage.value === 'pregnant') amount += Number(this.config.pregnantExtra) || 0;
          if (this.el.stage.value === 'breastfeeding') amount += Number(this.config.breastfeedingExtra) || 0;
        }
        return amount;
      }

      askStage(age) {
        return Boolean(age && age.canBePregnant) && this.sex() !== 'male';
      }

      /* ---------- Drawing ---------- */

      render() {
        const text = this.config.text;
        const age = this.ages.get(this.el.age.value) || this.config.ages[0];
        const female = this.sex() !== 'male';
        const amount = this.dailyAmount();
        const nrv = Number(this.config.nrv) || 375;

        const askStage = this.askStage(age);
        this.el.stageField.hidden = !askStage;
        if (!askStage) this.el.stage.value = 'none';

        this.el.amount.textContent = amount;
        this.el.supplementStat.hidden = !age.adult;

        const notes = [NrMagnesiumTool.fill(text.nrv, { amount: amount, nrv: nrv, percent: Math.round((amount / nrv) * 100) })];
        if (age.adult) {
          const average = Number(female ? this.config.averageFemale : this.config.averageMale);
          if (average > 0) notes.push(NrMagnesiumTool.fill(female ? text.averageFemale : text.averageMale, { average: average }));
          notes.push(NrMagnesiumTool.fill(text.supplement, { supplement: this.config.supplement }));
        } else {
          notes.push(text.child);
        }
        notes.push(text.kidney);
        NrMagnesiumTool.list(this.el.notes, notes);

        this.renderLabel(amount);
        this.renderGoal();

        this.el.foodShares.forEach((cell) => {
          const mg = Number(cell.dataset.foodShare) || 0;
          cell.textContent = amount > 0 ? Math.round((mg / amount) * 100) + '%' : '';
          cell.hidden = false;
        });
        if (this.el.foodShareHead) this.el.foodShareHead.hidden = false;
      }

      renderLabel(amount) {
        if (!this.el.form || !this.el.labelResult) return;
        const form = this.forms.get(this.el.form.value);
        const compound = Number(this.el.compound.value);
        if (!form || !(compound > 0)) {
          this.el.labelResult.replaceChildren();
          return;
        }
        const mg = Math.round((compound * Number(form.percent)) / 100);
        const text = this.config.text;
        NrMagnesiumTool.list(this.el.labelResult, [
          NrMagnesiumTool.fill(text.label, {
            compound: Math.round(compound),
            form: form.name,
            mg: mg,
            percent: amount > 0 ? Math.round((mg / amount) * 100) : 0,
          }),
          text.labelCheck,
        ]);
      }

      renderGoal() {
        if (!this.el.goalResult) return;
        const goal = this.goal();
        const text = this.config.text;
        const matches = this.config.forms.filter((form) => (form.goals || []).includes(goal));

        if (matches.length === 0) {
          NrMagnesiumTool.list(this.el.goalResult, [text.goalNone]);
          return;
        }

        this.el.goalResult.replaceChildren(
          ...matches.map((form) => {
            const li = document.createElement('li');
            li.append(NrMagnesiumTool.fill(text.goal, { form: form.name, note: form.bestFor }));
            if (form.url && text.readGuide) {
              const link = document.createElement('a');
              link.href = form.url;
              link.textContent = text.readGuide;
              li.append(' ', link);
            }
            return li;
          })
        );
      }

      /* ---------- Helpers ---------- */

      static get storageKey() {
        return 'nr-magnesium-tool';
      }

      static list(target, lines) {
        target.replaceChildren(
          ...lines
            .filter((line) => line)
            .map((line) => {
              const li = document.createElement('li');
              li.textContent = line;
              return li;
            })
        );
      }

      static fill(template, values) {
        return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
      }

      static publish(name, detail) {
        try {
          if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail);
        } catch (error) {
          /* Tracking must never break the tool. */
        }
      }
    }
  );
}
